import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  collection,
  getCountFromServer,
  limit,
  onSnapshot,
  orderBy,
  query,
  where,
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import PageHeader from '../components/PageHeader';
import { Card } from '../components/Form';
import { useDialog } from '../components/Dialog';
import StatusBadge from '../components/StatusBadge';
import { adminCancelOrderForce } from '../lib/api';
import {
  formatDateTime,
  locationAddress,
  money,
  orderTotal,
  shortAddress,
} from '../lib/format';

interface OrderRow {
  id: string;
  status?: string;
  [key: string]: unknown;
}

// Mirrors OrderStatus in packages/delivery_shared order_models.dart.
const STATUSES = [
  'all',
  'pending',
  'confirmed',
  'driverAssigned',
  'enRoute',
  'pickedUp',
  'inTransit',
  'delivered',
  'cancelled',
  'disputed',
];

const PAGE_SIZE = 50;

export default function OrdersPage() {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState('all');
  const [rows, setRows] = useState<OrderRow[]>([]);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [pageLimit, setPageLimit] = useState(PAGE_SIZE);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const { prompt, dialog } = useDialog();

  useEffect(() => {
    const base = collection(db, 'orders');
    const q =
      statusFilter === 'all'
        ? query(base, orderBy('createdAt', 'desc'), limit(pageLimit))
        : query(
            base,
            where('status', '==', statusFilter),
            orderBy('createdAt', 'desc'),
            limit(pageLimit)
          );
    const unsub = onSnapshot(
      q,
      (snap) => {
        setError(null);
        setRows(
          snap.docs.map((d) => ({ ...(d.data() as OrderRow), id: d.id }))
        );
      },
      (e) => setError(`Failed to load orders: ${e.message}`)
    );
    return unsub;
  }, [statusFilter, pageLimit]);

  // Total for the active filter (approximate; refreshed on filter change).
  useEffect(() => {
    const base = collection(db, 'orders');
    const q =
      statusFilter === 'all'
        ? query(base)
        : query(base, where('status', '==', statusFilter));
    let active = true;
    getCountFromServer(q)
      .then((snap) => {
        if (active) setTotalCount(snap.data().count);
      })
      .catch(() => {
        if (active) setTotalCount(null);
      });
    return () => {
      active = false;
    };
  }, [statusFilter]);

  const onForceCancel = async (id: string) => {
    const reason = await prompt({
      title: 'Force-cancel order',
      message: `Force-cancel order ${id}? Provide a reason for the audit log.`,
      placeholder: 'Reason',
      required: true,
      destructive: true,
      confirmLabel: 'Cancel order',
      cancelLabel: 'Keep order',
    });
    if (!reason) return;
    setBusyId(id);
    try {
      await adminCancelOrderForce({ orderId: id, reason });
    } catch (e) {
      setError(`Cancel failed: ${e instanceof Error ? e.message : e}`);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <>
      <PageHeader title="Orders" subtitle="Live-updating order feed." />
      {dialog}
      {error && (
        <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {error}
        </div>
      )}
      <Card>
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <label className="text-sm text-slate-600">
            Status:
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPageLimit(PAGE_SIZE);
              }}
              className="ml-2 rounded-md border border-slate-300 px-2 py-1 text-sm focus:border-brand-500 focus:outline-none"
            >
              {STATUSES.map((s) => (
                <option key={s}>{s}</option>
              ))}
            </select>
          </label>
          <span className="text-xs text-slate-400">
            {totalCount != null
              ? `Showing ${rows.length.toLocaleString()} of ${Math.max(
                  totalCount,
                  rows.length
                ).toLocaleString()}`
              : `${rows.length} order${rows.length === 1 ? '' : 's'}`}
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-3 py-2 text-left">Order</th>
                <th className="px-3 py-2 text-left">Created</th>
                <th className="px-3 py-2 text-left">Pickup</th>
                <th className="px-3 py-2 text-left">Drop-off</th>
                <th className="px-3 py-2 text-right">Total</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={r.id}
                  onClick={() => navigate(`/orders/${r.id}`)}
                  className="cursor-pointer border-t border-slate-100 hover:bg-slate-50/60"
                >
                  <td
                    className="px-3 py-2 font-mono text-xs text-slate-500"
                    title={r.id}
                  >
                    {r.id.slice(0, 8)}…
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 text-xs text-slate-500">
                    {formatDateTime(r.createdAt)}
                  </td>
                  <td
                    className="max-w-xs truncate px-3 py-2"
                    title={locationAddress(r, 'pickupLocation')}
                  >
                    {shortAddress(locationAddress(r, 'pickupLocation'))}
                  </td>
                  <td
                    className="max-w-xs truncate px-3 py-2"
                    title={locationAddress(r, 'deliveryLocation')}
                  >
                    {shortAddress(locationAddress(r, 'deliveryLocation'))}
                  </td>
                  <td className="px-3 py-2 text-right font-medium tabular-nums">
                    {money(orderTotal(r))}
                  </td>
                  <td className="px-3 py-2">
                    <StatusBadge status={r.status} />
                  </td>
                  <td className="px-3 py-2 text-right">
                    {r.status !== 'cancelled' && r.status !== 'delivered' && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onForceCancel(r.id);
                        }}
                        disabled={busyId === r.id}
                        className="rounded-md border border-red-200 px-2 py-1 text-xs font-medium text-red-700 transition hover:bg-red-50 disabled:opacity-60"
                      >
                        Force cancel
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td
                    colSpan={7}
                    className="px-3 py-8 text-center text-sm text-slate-500"
                  >
                    No orders found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {(totalCount != null
          ? rows.length < totalCount
          : rows.length >= pageLimit) && (
          <div className="mt-4 text-center">
            <button
              onClick={() => setPageLimit((n) => n + PAGE_SIZE)}
              className="rounded-md border border-slate-300 px-4 py-1.5 text-sm font-medium text-slate-600 transition hover:bg-slate-50"
            >
              Load more
            </button>
          </div>
        )}
      </Card>
    </>
  );
}
